import React, { useState, useMemo } from 'react';
import {
  ClockCounterClockwise,
  File,
  ArrowSquareOut,
  CaretDown,
  CaretRight,
  Sparkle,
  SpinnerGap,
  Calendar,
  CheckCircle,
  XCircle,
  Warning,
  Clock,
} from '@phosphor-icons/react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

interface TimelineDocument {
  id: number;
  fileName?: string;
  label?: string[];
  sharePointFileUrl?: string;
  documentDate?: string;
  expirationDate?: string;
  summary?: string;
  status?: string;
  createdAt: string;
}

interface CumulativeTimelineProps {
  documents: TimelineDocument[];
  isLoading?: boolean;
  synthesis?: string;
  isSynthesisLoading?: boolean;
  onGenerateSynthesis?: () => void;
  labelName: string;
}

/**
 * Get status icon and color based on document status
 */
const getStatusDisplay = (status?: string) => {
  const lowerStatus = status?.toLowerCase();

  if (!status) {
    return { icon: Clock, color: 'text-neutral-400', bgColor: 'bg-neutral-100' };
  }

  // Positive statuses
  if (
    lowerStatus?.includes('verleend') ||
    lowerStatus?.includes('goedgekeurd') ||
    lowerStatus?.includes('vergund') ||
    lowerStatus?.includes('akkoord') ||
    lowerStatus?.includes('approved')
  ) {
    return { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100' };
  }

  // Negative statuses
  if (
    lowerStatus?.includes('geweigerd') ||
    lowerStatus?.includes('afgekeurd') ||
    lowerStatus?.includes('rejected') ||
    lowerStatus?.includes('denied')
  ) {
    return { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100' };
  }

  // Pending/warning statuses
  if (
    lowerStatus?.includes('pending') ||
    lowerStatus?.includes('in behandeling') ||
    lowerStatus?.includes('aangevraagd')
  ) {
    return { icon: Warning, color: 'text-amber-600', bgColor: 'bg-amber-100' };
  }

  return { icon: File, color: 'text-blue-600', bgColor: 'bg-blue-100' };
};

/**
 * CumulativeTimeline - Displays a chronological timeline of documents for a cumulative label
 */
const CumulativeTimeline: React.FC<CumulativeTimelineProps> = ({
  documents,
  isLoading,
  synthesis,
  isSynthesisLoading,
  onGenerateSynthesis,
  labelName,
}) => {
  const [expandedDocs, setExpandedDocs] = useState<Set<number>>(new Set());
  const [showSynthesis, setShowSynthesis] = useState(true);

  // Sort documents by date (newest first)
  const sortedDocs = useMemo(() => {
    return [...documents].sort((a, b) => {
      const dateA = new Date(a.documentDate || a.createdAt);
      const dateB = new Date(b.documentDate || b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });
  }, [documents]);

  const toggleDoc = (docId: number) => {
    setExpandedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-violet-700">
          <ClockCounterClockwise weight="duotone" size={18} />
          <span className="font-medium">Documentengeschiedenis</span>
        </div>
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-sm text-neutral-secondary-mid py-4 text-center">
        Geen documenten gevonden voor dit label
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-violet-700">
          <ClockCounterClockwise weight="duotone" size={18} />
          <span className="font-medium">
            Documentengeschiedenis: {labelName}
          </span>
          <span className="text-xs text-neutral-secondary-mid">
            ({documents.length} {documents.length === 1 ? 'document' : 'documenten'})
          </span>
        </div>
      </div>

      {/* Synthesis section */}
      {(synthesis || onGenerateSynthesis) && (
        <div className="bg-gradient-to-r from-violet-50 to-blue-50 rounded-lg border border-violet-200 p-3">
          <button
            onClick={() => setShowSynthesis(!showSynthesis)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2 text-violet-700">
              <Sparkle weight="fill" size={16} />
              <span className="font-medium text-sm">AI Samenvatting</span>
            </div>
            {showSynthesis ? (
              <CaretDown size={14} className="text-violet-500" />
            ) : (
              <CaretRight size={14} className="text-violet-500" />
            )}
          </button>

          {showSynthesis && (
            <div className="mt-2">
              {isSynthesisLoading ? (
                <div className="flex items-center gap-2 text-sm text-violet-600">
                  <SpinnerGap size={14} className="animate-spin" />
                  <span>Samenvatting genereren...</span>
                </div>
              ) : synthesis ? (
                <p className="text-sm text-neutral-700 leading-relaxed">
                  {synthesis}
                </p>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onGenerateSynthesis}
                    className="text-xs h-7"
                  >
                    <Sparkle size={12} className="mr-1" />
                    Genereer samenvatting
                  </Button>
                  <span className="text-xs text-neutral-secondary-mid">
                    Combineert alle documenten tot een overzicht
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Timeline */}
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-violet-200" />

        {/* Document entries */}
        <div className="space-y-3">
          {sortedDocs.map((doc, index) => {
            const isExpanded = expandedDocs.has(doc.id);
            const statusDisplay = getStatusDisplay(doc.status);
            const StatusIcon = statusDisplay.icon;
            const isLatest = index === 0;
            const isExpired = doc.expirationDate && new Date(doc.expirationDate) < new Date();

            return (
              <div
                key={doc.id}
                className={`relative pl-10 ${
                  isLatest ? 'opacity-100' : 'opacity-90'
                }`}
              >
                {/* Timeline dot */}
                <div
                  className={`absolute left-2 top-3 w-5 h-5 rounded-full flex items-center justify-center ${
                    isLatest ? 'bg-violet-500' : statusDisplay.bgColor
                  }`}
                >
                  <StatusIcon
                    size={12}
                    weight="fill"
                    className={isLatest ? 'text-white' : statusDisplay.color}
                  />
                </div>

                {/* Document card */}
                <div
                  className={`bg-white rounded-lg border ${
                    isLatest
                      ? 'border-violet-300 shadow-sm'
                      : 'border-neutral-200'
                  } overflow-hidden`}
                >
                  <button
                    onClick={() => toggleDoc(doc.id)}
                    className="w-full px-3 py-2 text-left hover:bg-neutral-50/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <File
                          size={14}
                          weight="duotone"
                          className={isLatest ? 'text-violet-500' : 'text-neutral-400'}
                        />
                        <span
                          className={`text-sm truncate ${
                            isLatest ? 'font-medium' : ''
                          }`}
                        >
                          {doc.fileName || doc.label?.[0] || `Document #${doc.id}`}
                        </span>
                        {isLatest && (
                          <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-medium">
                            Meest recent
                          </span>
                        )}
                        {doc.status && (
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded ${statusDisplay.bgColor} ${statusDisplay.color}`}
                          >
                            {doc.status}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-neutral-secondary-mid flex-shrink-0">
                        <Calendar size={12} />
                        <span>
                          {doc.documentDate
                            ? new Date(doc.documentDate).toLocaleDateString('nl-NL')
                            : new Date(doc.createdAt).toLocaleDateString('nl-NL')}
                        </span>
                        {isExpanded ? (
                          <CaretDown size={12} />
                        ) : (
                          <CaretRight size={12} />
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-1 border-t border-neutral-100 space-y-2">
                      {/* Summary */}
                      {doc.summary && (
                        <p className="text-xs text-neutral-600 leading-relaxed">
                          {doc.summary}
                        </p>
                      )}

                      {/* Dates */}
                      <div className="flex gap-4 text-xs">
                        {doc.documentDate && (
                          <span className="text-neutral-secondary-mid">
                            Documentdatum:{' '}
                            <span className="text-neutral-700">
                              {new Date(doc.documentDate).toLocaleDateString('nl-NL')}
                            </span>
                          </span>
                        )}
                        {doc.expirationDate && (
                          <span
                            className={
                              isExpired
                                ? 'text-red-600'
                                : 'text-neutral-secondary-mid'
                            }
                          >
                            Vervaldatum:{' '}
                            <span className={isExpired ? 'font-medium' : 'text-neutral-700'}>
                              {new Date(doc.expirationDate).toLocaleDateString('nl-NL')}
                              {isExpired && ' (verlopen)'}
                            </span>
                          </span>
                        )}
                      </div>

                      {/* Open link */}
                      {doc.sharePointFileUrl && (
                        <a
                          href={doc.sharePointFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span>Openen in SharePoint</span>
                          <ArrowSquareOut size={10} />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CumulativeTimeline;
